// Kit charm variant picker.
// A small centered popup that lets the customer choose the variant of a charm before it goes
// into the kit. Triggered by <kit-builder> when the customer hits + on a charm that has more
// than one variant. Receives the product object + target slot, renders a list of variants,
// and dispatches `kit-charm-drawer:add` with the chosen variant when confirmed.

if (!customElements.get('kit-charm-drawer')) {
  customElements.define(
    'kit-charm-drawer',
    class KitCharmDrawer extends HTMLElement {
      constructor() {
        super();

        this.contentEl = this.querySelector('[data-charm-drawer-content]');
        this.overlayEl = this.querySelector('[data-charm-drawer-overlay]');
        this.closeBtn = this.querySelector('[data-charm-drawer-close]');

        this.currentSlot = null;
        this.currentProduct = null;
        this.selectedVariantId = null;

        // Move ourselves to <body> so `position: fixed` is anchored to the viewport, not to a
        // section ancestor that may create a containing block.
        if (this.parentElement !== document.body) {
          document.body.appendChild(this);
        }

        document.addEventListener('kit-charm-drawer:open', (ev) => this.open(ev.detail));
        if (this.overlayEl) this.overlayEl.addEventListener('click', () => this.close());
        if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
        document.addEventListener('keydown', (e) => {
          if (e.key === 'Escape' && this.classList.contains('is-open')) this.close();
        });

        this.contentEl.addEventListener('click', (e) => {
          const variantBtn = e.target.closest('[data-variant-id]');
          if (variantBtn && !variantBtn.disabled) {
            this.selectedVariantId = parseInt(variantBtn.dataset.variantId);
            // Visual feedback right before the popup closes.
            this.contentEl.querySelectorAll('[data-variant-id]').forEach((b) => {
              b.classList.toggle('is-selected', b === variantBtn);
            });
            // One-shot: clicking a variant adds it directly and closes the popup.
            this._confirm();
          }
        });
      }

      open({ product, slot }) {
        if (!product) return;
        this.currentProduct = product;
        this.currentSlot = slot || 1;
        this.selectedVariantId = null;

        this.contentEl.innerHTML = this._render(product);
        this.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }

      close() {
        this.classList.remove('is-open');
        document.body.style.overflow = '';
        this.contentEl.innerHTML = '';
        this.currentProduct = null;
        this.currentSlot = null;
        this.selectedVariantId = null;
      }

      _render(product) {
        const imageUrl = typeof product.featured_image === 'string' ? product.featured_image : '';
        const variantsHtml = product.variants
          .map((v) => {
            const disabled = !v.available;
            const isSelected = v.id === this.selectedVariantId;
            const variantImg = (typeof v.featured_image === 'string' && v.featured_image) || imageUrl;
            const label = v.title === 'Default Title' ? product.title : v.title;
            return `
              <button
                type="button"
                class="kit-charm-popup__variant${isSelected ? ' is-selected' : ''}${disabled ? ' is-disabled' : ''}"
                data-variant-id="${v.id}"
                ${disabled ? 'disabled' : ''}
              >
                ${variantImg ? `<img src="${variantImg}" alt="" loading="lazy">` : ''}
                <span class="kit-charm-popup__variant-label">${this._escape(label)}</span>
                ${disabled ? '<span class="kit-charm-popup__variant-soldout">Épuisé</span>' : ''}
              </button>`;
          })
          .join('');

        return `
          <div class="kit-charm-popup">
            <div class="kit-charm-popup__head">
              ${imageUrl ? `<div class="kit-charm-popup__thumb"><img src="${imageUrl}" alt="${this._escape(product.title)}" loading="lazy"></div>` : ''}
              <div class="kit-charm-popup__head-info">
                <h3 class="kit-charm-popup__title">${this._escape(product.title)}</h3>
                <p class="kit-charm-popup__hint">Choisis la variante à ajouter</p>
              </div>
            </div>
            <div class="kit-charm-popup__variants">${variantsHtml}</div>
          </div>
        `;
      }

      _confirm() {
        if (!this.currentProduct || !this.selectedVariantId) return;
        const variant = this.currentProduct.variants.find((v) => v.id === this.selectedVariantId);
        if (!variant) return;

        const variantImage = (typeof variant.featured_image === 'string' && variant.featured_image)
          || this.currentProduct.featured_image
          || '';

        document.dispatchEvent(new CustomEvent('kit-charm-drawer:add', {
          detail: {
            slot: this.currentSlot,
            variantId: variant.id,
            productId: this.currentProduct.id,
            title: this.currentProduct.title,
            variantTitle: variant.title !== 'Default Title' ? variant.title : null,
            price: variant.price,
            image: variantImage,
          },
        }));

        this.close();
      }

      _escape(str) {
        if (!str) return '';
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
      }
    }
  );
}
